import { MAX_ATTACHMENT_BYTES, UPLOAD_PART_BYTES } from '../../../../shared/uploadLimits';
import { uploadContextMultipart } from './context-upload';
/**
 * API client — adapter from the original PP/arx server contract to
 * the new CF Worker API.
 *
 * Strategy:
 *   - Keep the exported function signatures identical so the rest of
 *     the frontend (PP-copied components) needs no changes.
 *   - Map each call to the equivalent CF endpoint where one exists.
 *   - Where the backend isn't there yet (folders, phase, mode,
 *     versions, members, profiles, share, context, Feishu,
 *     admin), throw `NotImplementedError` with a useful message OR
 *     return a sensible default (e.g. empty list, synthetic single
 *     owner) so the UI keeps rendering.
 *   - Never return the PP {ok, data} envelope — CF returns raw JSON;
 *     errors come back as `{error: {code, message}}` and we throw.
 */

import type { Deck, Message, SSEEvent, Profile, Folder } from '../types';
import { fetchMeShared, bustMeCache } from './me-shared';

// ── Burst dedupe for idempotent GETs ─────────────────────────────
// A cold page load fires the same list endpoints from several
// independent effects/components within the same second (observed:
// 5x /workspaces/:id/decks, 2x /profiles, knowledge connections
// per refresh). Identical GETs within a short window share one
// request. Mutating helpers call bustGetCache() so a post-mutation
// refresh never serves a pre-mutation answer.
const GET_DEDUP_TTL_MS = 2_000;
const _getCache = new Map<string, { at: number; p: Promise<unknown> }>();

function dedupGet<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const hit = _getCache.get(key);
  if (hit && Date.now() - hit.at < GET_DEDUP_TTL_MS) return hit.p as Promise<T>;
  const p = fn().catch((err) => {
    _getCache.delete(key);
    throw err;
  });
  _getCache.set(key, { at: Date.now(), p });
  return p;
}

export function bustGetCache(prefix?: string): void {
  if (!prefix) {
    _getCache.clear();
    return;
  }
  for (const k of _getCache.keys()) if (k.startsWith(prefix)) _getCache.delete(k);
}

import { apiFetch, apiUpload } from './auth-fetch';

const API = '/api';

export class NotImplementedError extends Error {
  constructor(feature: string) {
    super(`${feature} is not implemented in this build yet.`);
    this.name = 'NotImplementedError';
  }
}

interface CfErrorBody {
  error?: { code: string; message: string };
}

async function cfRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const authEntry = path === '/auth/login' || path === '/auth/register';
  const res = await (authEntry ? fetch(`${API}${path}`, {...init, credentials:'include'}) : apiFetch(`${API}${path}`, init));
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as CfErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ── Auth ─────────────────────────────────────────────────────
//
// PP's login took (username, password) and returned
// {token, user_id, username, role}. We adapt: treat `username` as
// email (PP's UI is loosely email-shaped already), and synthesize
// the token-shape from the CF /me response.

export async function login(emailOrUsername: string, password: string) {
  bustMeCache();
  const res = await cfRequest<{
    user: {
      id: string;
      email: string;
      display_name: string | null;
      avatar_url: string | null;
    };
  }>('POST', '/auth/login', { email: emailOrUsername, password });
  return {
    token: 'cookie-auth',
    user_id: res.user.id,
    username: res.user.display_name || res.user.email,
    role: 'user',
  };
}

export async function register(
  username: string,
  email: string,
  password: string,
) {
  bustMeCache();
  const res = await cfRequest<{
    user: {
      id: string;
      email: string;
      display_name: string | null;
      avatar_url: string | null;
    };
  }>('POST', '/auth/register', {
    email: email || username,
    password,
    display_name: username,
  });
  return {
    token: 'cookie-auth',
    user_id: res.user.id,
    username: res.user.display_name || res.user.email,
    role: 'user',
  };
}

// ── Workspaces ───────────────────────────────────────────────

export interface Workspace {
  id: string;
  name: string;
  type: 'personal' | 'team';
  owner_id: string;
  my_role: string;
  avatar_url?: string;
  created_at: number;
  updated_at: number;
}

interface CfWorkspace {
  id: string;
  name: string;
  owner_id: string;
  created_at: number;
}

function adaptWorkspace(w: CfWorkspace, currentUserId?: string): Workspace {
  return {
    id: w.id,
    name: w.name,
    owner_id: w.owner_id,
    type: 'personal',
    my_role: currentUserId === w.owner_id ? 'owner' : 'member',
    created_at: w.created_at,
    updated_at: w.created_at,
  };
}

export async function listWorkspaces(): Promise<Workspace[]> {
  const me = await fetchMeShared();
  return (me.workspaces as CfWorkspace[]).map((w) => adaptWorkspace(w, me.user.id));
}

export async function listWorkspaceDecks(wsId: string): Promise<Deck[]> {
  return dedupGet(`decks:${wsId}`, async () => {
    const res = await cfRequest<{ decks: CfDeck[] }>(
      'GET',
      `/workspaces/${wsId}/decks`,
    );
    return res.decks.map(adaptDeck);
  });
}

export async function createWorkspace(name: string): Promise<Workspace> {
  bustGetCache('decks:');
  const res = await cfRequest<{ workspace: CfWorkspace }>('POST', '/workspaces', {
    name,
  });
  return adaptWorkspace(res.workspace, res.workspace.owner_id);
}

// ── Decks ────────────────────────────────────────────────────

interface CfDeckSignals {
  is_actively_working: boolean;
  turn_status?: 'idle' | 'running';
  turn_id?: string | null;
  started_at?: number | null;
  /** Generic artifact viewer (PP main #87 alignment). */
  artifacts?: CfArtifactSignal[];
  active_artifact_id?: string | null;
}

interface CfArtifactSignal {
  id: string;
  display_name: string;
  mime: string;
  kind: 'deck-group' | 'file';
  source_format?: 'html' | 'pptx-template' | 'pptx-svg';
  version_label: string;
  size?: number;
  updated_at: number;
  download_url: string;
  preview_url: string;
}

interface CfDeck {
  id: string;
  workspace_id: string;
  owner_id: string;
  title: string;
  phase?: string;
  mode?: string | null;
  theme?: string | null;
  folder_id?: string | null;
  created_at: number;
  updated_at: number;
  signals?: CfDeckSignals;
}

function adaptDeck(d: CfDeck): Deck {
  return {
    workspace_id: d.workspace_id,
    id: d.id,
    arx_conversation_id: d.id,
    user_id: d.owner_id,
    title: d.title,
    phase: (d.phase as Deck['phase']) ?? 'gather',
    mode: (d.mode as Deck['mode']) ?? null,
    theme: (d.theme as Deck['theme']) ?? null,
    disabled_context_keys: null,
    folder_id: d.folder_id ?? null,
    created_at: d.created_at,
    updated_at: d.updated_at,
    ...(d.signals && typeof d.signals.is_actively_working === 'boolean'
      ? {
          signals: {
            is_actively_working: d.signals.is_actively_working,
            turn_status: d.signals.turn_status,
            turn_id: d.signals.turn_id ?? null,
            started_at: d.signals.started_at ?? null,
            artifacts: d.signals.artifacts ?? [],
            active_artifact_id: d.signals.active_artifact_id ?? null,
          },
        }
      : {}),
  };
}

export const createDeck = async (
  title?: string,
  _narrative?: string,
  _style?: string,
  _templateRef?: string,
  folderId?: string,
  workspaceId?: string,
): Promise<Deck> => {
  bustGetCache('decks:');
  const wsId = workspaceId ?? (await listWorkspaces())[0]?.id;
  if (!wsId) throw new Error('No workspace available.');
  const res = await cfRequest<{ deck: CfDeck }>(
    'POST',
    `/workspaces/${wsId}/decks`,
    {
      title: title || 'Untitled deck',
      ...(folderId ? { folder_id: folderId } : {}),
    },
  );
  return adaptDeck(res.deck);
};

export async function listDecks(workspaceId?: string): Promise<Deck[]> {
  if (workspaceId) return listWorkspaceDecks(workspaceId);
  const all: Deck[] = [];
  for (const ws of await listWorkspaces()) {
    all.push(...(await listWorkspaceDecks(ws.id)));
  }
  return all;
}

async function _getDeckImpl(id: string): Promise<Deck> {
  const res = await cfRequest<{ deck: CfDeck }>('GET', `/decks/${id}`);
  return adaptDeck(res.deck);
}

export async function updateDeck(
  id: string,
  updates: Partial<Deck>,
): Promise<Deck> {
  const patch: Record<string, unknown> = {};
  if (updates.title !== undefined) patch.title = updates.title;
  if (updates.folder_id !== undefined) patch.folder_id = updates.folder_id;
  const res = await cfRequest<{ deck: CfDeck }>('PATCH', `/decks/${id}`, patch);
  return adaptDeck(res.deck);
}

export async function deleteDeck(id: string): Promise<void> {
  bustGetCache('decks:');
  await cfRequest<{ ok: true }>('DELETE', `/decks/${id}`);
}

export async function clearMessages(_deckId: string): Promise<void> {
  throw new NotImplementedError('Clear messages');
}

export async function warmupDeck(
  _deckId: string,
): Promise<{ tulpas: string[] }> {
  // CF doesn't have warmup — agent is invoked lazily on first /send.
  return { tulpas: [] };
}

export async function advancePhase(id: string, phase: string): Promise<Deck> {
  const res = await cfRequest<{ deck: CfDeck }>('POST', `/decks/${id}/phase`, {
    phase,
  });
  return adaptDeck(res.deck);
}

// ── Build mode ───────────────────────────────────────────────

// DeckMode + DeckTheme + setDeckMode + PATCH /decks/:id/mode removed
// in Stage 4 of #87 alignment. Mode is per-turn agent decision now
// (signals.artifacts.source_format), not a deck-level column.

// ── Versions ─────────────────────────────────────────────────

export interface DeckVersion {
  key: string;
  title: string;
  slides: number;
  created_at: number;
  updated_at: number;
}

async function _listVersionsImpl(
  deckId: string,
): Promise<{ versions: DeckVersion[]; current: string }> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { versions: DeckVersion[]; current: string };
  }>('GET', `/decks/${deckId}/versions`);
  return {
    versions: res.data?.versions ?? [],
    current: res.data?.current ?? '',
  };
}

export async function createVersion(
  _deckId: string,
  _name: string,
): Promise<{ version: string }> {
  // CF rebuild materialises versions automatically when the agent
  // emits a slide-formatted reply — there's no explicit create call.
  throw new NotImplementedError('Manual version creation');
}

export async function switchVersion(
  deckId: string,
  name: string,
): Promise<{ current: string }> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { current: string };
  }>('PUT', `/decks/${deckId}/versions/current`, { name });
  return { current: res.data?.current ?? name };
}

export interface SlideRow {
  presentation_name: string;
  slide_num: number;
  title: string;
  body: string;
  updated_at: number;
}

export async function getVersion(
  deckId: string,
  name: string,
): Promise<{
  presentation: { name: string; title: string | null; slides_count: number };
  slides: SlideRow[];
}> {
  const res = await cfRequest<{
    ok: boolean;
    data?: {
      presentation: { name: string; title: string | null; slides_count: number };
      slides: SlideRow[];
    };
  }>('GET', `/decks/${deckId}/versions/${encodeURIComponent(name)}`);
  if (!res.data) throw new Error('Empty version response');
  return res.data;
}

export async function deleteVersion(
  deckId: string,
  name: string,
): Promise<void> {
  await cfRequest('DELETE', `/decks/${deckId}/versions/${encodeURIComponent(name)}`);
}

export async function saveSlide(
  deckId: string,
  versionName: string,
  patch: { slide_num: number; title?: string; body?: string },
): Promise<SlideRow> {
  const res = await cfRequest<{
    ok: boolean;
    data?: { slide: SlideRow };
  }>(
    'PUT',
    `/decks/${deckId}/versions/${encodeURIComponent(versionName)}/slide`,
    patch,
  );
  if (!res.data) throw new Error('Empty slide response');
  return res.data.slide;
}

export function exportPptxUrl(deckId: string, version?: string | null): string {
  const v = version ? `?version=${encodeURIComponent(version)}` : '';
  return `${API}/decks/${deckId}/export.pptx${v}`;
}

// ── Chat attachments (multimodal input) ──────────────────────

export interface ChatAttachment {
  r2_key: string;
  mime: string;
  size: number;
  name: string;
}

/** Allowed input MIMEs. Matches the server allow list. */
export const CHAT_ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/jpg,image/gif,image/webp';

export const CHAT_ATTACHMENT_MAX_BYTES = 3_500_000;
export const CHAT_ATTACHMENT_MAX_PER_TURN = 5;

export async function uploadChatAttachment(
  deckId: string,
  file: File,
): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API}/decks/${deckId}/chat-attachments`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = body?.error?.message || res.statusText || 'Upload failed';
    throw new Error(msg);
  }
  return (await res.json()) as ChatAttachment;
}

export function chatAttachmentDownloadUrl(
  deckId: string,
  r2Key: string,
): string {
  return `${API}/decks/${deckId}/chat-attachments?key=${encodeURIComponent(r2Key)}`;
}

// ── Tulpas ───────────────────────────────────────────────────

export interface PpTulpa {
  id: string;
  slug: string;
  name: string;
  workspace_id: string | null;
  system_prompt: string;
  description: string | null;
  is_default: boolean;
  is_system: boolean;
  talents: { id: string; slug: string; sort_order: number }[];
  created_at: number;
  updated_at: number;
}

export async function listPpTulpas(workspaceId: string): Promise<PpTulpa[]> {
  const res = await cfRequest<{ tulpas: PpTulpa[] }>(
    'GET',
    `/tulpas?ws=${encodeURIComponent(workspaceId)}`,
  );
  return res.tulpas;
}

export async function createPpTulpa(
  workspaceId: string,
  input: {
    slug: string;
    name: string;
    system_prompt: string;
    description?: string | null;
    talent_slugs?: string[];
  },
): Promise<PpTulpa> {
  const res = await cfRequest<{ tulpa: PpTulpa }>(
    'POST',
    `/tulpas?ws=${encodeURIComponent(workspaceId)}`,
    input,
  );
  return res.tulpa;
}

export async function updatePpTulpa(
  workspaceId: string,
  slug: string,
  patch: {
    name?: string;
    system_prompt?: string;
    description?: string | null;
    is_default?: boolean;
    talent_slugs?: string[];
  },
): Promise<PpTulpa> {
  const res = await cfRequest<{ tulpa: PpTulpa }>(
    'PATCH',
    `/tulpas/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
    patch,
  );
  return res.tulpa;
}

export async function deletePpTulpa(
  workspaceId: string,
  slug: string,
): Promise<void> {
  await cfRequest(
    'DELETE',
    `/tulpas/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
  );
}

// ── Talents ──────────────────────────────────────────────────

export interface PpTalent {
  id: string;
  slug: string;
  name: string;
  workspace_id: string | null;
  is_system: boolean;
  kind: 'prompt' | 'script';
  runtime: string | null;
  entry: string | null;
  description: string | null;
  files: { path: string; size: number }[];
  created_at: number;
  updated_at: number;
}

export async function listPpTalents(workspaceId: string): Promise<PpTalent[]> {
  const res = await cfRequest<{ talents: PpTalent[] }>(
    'GET',
    `/talents?ws=${encodeURIComponent(workspaceId)}`,
  );
  return res.talents;
}

export async function createPromptTalent(
  workspaceId: string,
  input: {
    slug: string;
    name: string;
    body: string;
    description?: string | null;
  },
): Promise<PpTalent> {
  const res = await cfRequest<{ talent: PpTalent }>(
    'POST',
    `/talents?ws=${encodeURIComponent(workspaceId)}`,
    input,
  );
  return res.talent;
}

// Multipart upload for script-style talents.
export async function uploadScriptTalent(
  workspaceId: string,
  meta: {
    slug: string;
    name: string;
    runtime?: string;
    entry?: string;
    description?: string | null;
  },
  files: { path: string; file: File }[],
): Promise<PpTalent> {
  const fd = new FormData();
  fd.set('slug', meta.slug);
  fd.set('name', meta.name);
  fd.set('kind', 'script');
  if (meta.runtime) fd.set('runtime', meta.runtime);
  if (meta.entry) fd.set('entry', meta.entry);
  if (meta.description != null) fd.set('description', meta.description);
  for (const f of files) {
    fd.append(f.path, f.file, f.path);
  }
  const res = await apiFetch(
    `${API}/talents?ws=${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      body: fd,
    },
  );
  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as CfErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(message);
  }
  const body = (await res.json()) as { talent: PpTalent };
  return body.talent;
}

export async function deletePpTalent(
  workspaceId: string,
  slug: string,
): Promise<void> {
  await cfRequest(
    'DELETE',
    `/talents/${encodeURIComponent(slug)}?ws=${encodeURIComponent(workspaceId)}`,
  );
}

// ── Deck ↔ tulpa binding + manual talent run ─────────────────

export async function bindDeckTulpa(
  deckId: string,
  tulpaSlug: string | null,
): Promise<void> {
  await cfRequest('PUT', `/decks/${deckId}/tulpa`, {
    tulpa_slug: tulpaSlug,
  });
}

export interface TalentRunOutputClient {
  path: string;
  size: number;
  r2_key: string;
  content_type?: string;
}

export interface TalentRunResultClient {
  run_id: string;
  talent_slug: string;
  exit_code: number;
  stdout_excerpt: string;
  stderr_excerpt: string;
  duration_ms: number;
  outputs: TalentRunOutputClient[];
}

export async function runTalent(
  deckId: string,
  input: {
    talent_slug: string;
    args?: Record<string, string>;
    input_keys?: string[];
  },
): Promise<TalentRunResultClient> {
  const res = await cfRequest<{
    ok: boolean;
    data: TalentRunResultClient;
    error?: string;
  }>('POST', `/decks/${deckId}/talents/run`, input);
  if (!res.ok) throw new Error(res.error ?? 'Talent run failed');
  return res.data;
}

// ── Members ──────────────────────────────────────────────────

export interface DeckMember {
  id: string;
  deck_id: string;
  member_id: string;
  member_type: 'human' | 'agent';
  role: string;
  tulpa_id?: string;
  invited_by?: string | null;
  joined_at: number;
  status: string;
  // Hydrated by the server (PR #16 onwards).
  email?: string;
  display_name?: string | null;
  avatar_url?: string | null;
}

// Wire shape from the CF backend (PR #16).
interface CfDeckMember {
  deck_id: string;
  user_id: string;
  role: 'owner' | 'editor' | 'viewer';
  invited_by: string | null;
  created_at: number;
  updated_at: number;
  user: {
    email: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

function adaptDeckMember(deckId: string, m: CfDeckMember): DeckMember {
  return {
    id: `${deckId}-${m.user_id}`,
    deck_id: m.deck_id,
    member_id: m.user_id,
    member_type: 'human',
    role: m.role,
    invited_by: m.invited_by,
    joined_at: m.created_at,
    status: 'active',
    email: m.user?.email,
    display_name: m.user?.display_name,
    avatar_url: m.user?.avatar_url,
  };
}

export interface DeckAgent {
  tulpa_id: string;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
}

export type DeckChatMode = 'group' | 'relay';

async function _listDeckAgentsImpl(
  deckId: string,
): Promise<{ agents: DeckAgent[]; chat_mode: DeckChatMode }> {
  const res = await cfRequest<{
    ok: boolean;
    agents: DeckAgent[];
    chat_mode?: DeckChatMode;
  }>('GET', `/decks/${deckId}/agents`);
  return { agents: res.agents ?? [], chat_mode: res.chat_mode ?? 'group' };
}

export async function setDeckAgents(
  deckId: string,
  tulpa_slugs: string[],
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'PUT',
    `/decks/${deckId}/agents`,
    { tulpa_slugs },
  );
  return res.agents ?? [];
}

export async function addDeckAgent(
  deckId: string,
  slug: string,
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'POST',
    `/decks/${deckId}/agents/${encodeURIComponent(slug)}`,
  );
  return res.agents ?? [];
}

export async function removeDeckAgent(
  deckId: string,
  slug: string,
): Promise<DeckAgent[]> {
  const res = await cfRequest<{ ok: boolean; agents: DeckAgent[] }>(
    'DELETE',
    `/decks/${deckId}/agents/${encodeURIComponent(slug)}`,
  );
  return res.agents ?? [];
}

export async function setDeckChatMode(
  deckId: string,
  mode: DeckChatMode,
): Promise<void> {
  await cfRequest('PUT', `/decks/${deckId}/chat-mode`, { mode });
}

async function _listDeckMembersImpl(deckId: string): Promise<DeckMember[]> {
  // Workspace-level human members + per-deck agent roster. Both
  // surface as DeckMember rows so the popover renders them in one
  // pane. Agents come from the deck_agents table (Slice 8 schema);
  // previously the client synthesized a single agent row from
  // decks.tulpa_id, which couldn't represent a multi-tulpa roster.
  //
  // Used to also fetch /decks/:id here just for deck.created_at →
  // assigned to every agent row's joined_at. That third GET added a
  // ~1.6s parallel call on every deck-open hot path. Replaced with
  // the owner's deck_members.created_at (decks + deck_members are
  // written in the same D1 batch in createDeck, so the two values
  // are equal on freshly-created decks and "close enough" on legacy
  // ones — joined_at on agent rows is decorative; nothing branches
  // on its exact value).
  const [humansRes, agentsRes] = await Promise.all([
    cfRequest<{ members: CfDeckMember[] }>('GET', `/decks/${deckId}/members`),
    listDeckAgents(deckId),
  ]);
  const humans = humansRes.members.map((m) => adaptDeckMember(deckId, m));
  const owner = humansRes.members.find((m) => m.role === 'owner');
  const agentJoinedAt = owner?.created_at ?? humansRes.members[0]?.created_at ?? 0;
  const agents: DeckMember[] = agentsRes.agents.map((a) => ({
    id: `${deckId}-agent-${a.slug}`,
    deck_id: deckId,
    member_id: a.tulpa_id,
    member_type: 'agent',
    role: 'agent',
    tulpa_id: a.slug,
    joined_at: agentJoinedAt,
    status: 'idle',
  }));
  return [...humans, ...agents];
}

export async function inviteDeckMember(
  deckId: string,
  input: { email?: string; user_id?: string; role?: 'owner' | 'editor' | 'viewer' },
): Promise<DeckMember> {
  const res = await cfRequest<{ member: CfDeckMember }>(
    'POST',
    `/decks/${deckId}/members`,
    input,
  );
  return adaptDeckMember(deckId, res.member);
}

export async function setDeckMemberRole(
  deckId: string,
  userId: string,
  role: 'owner' | 'editor' | 'viewer',
): Promise<DeckMember> {
  const res = await cfRequest<{ member: CfDeckMember }>(
    'PATCH',
    `/decks/${deckId}/members/${encodeURIComponent(userId)}`,
    { role },
  );
  return adaptDeckMember(deckId, res.member);
}

export async function removeDeckMember(
  deckId: string,
  userId: string,
): Promise<void> {
  await cfRequest('DELETE', `/decks/${deckId}/members/${encodeURIComponent(userId)}`);
}

export async function transferDeckOwner(
  deckId: string,
  newOwnerId: string,
): Promise<DeckMember[]> {
  const res = await cfRequest<{ members: CfDeckMember[] }>(
    'POST',
    `/decks/${deckId}/members/${encodeURIComponent(newOwnerId)}/transfer-owner`,
  );
  return res.members.map((m) => adaptDeckMember(deckId, m));
}

// ── Messages ─────────────────────────────────────────────────

interface CfMessage {
  author_user_id?:string;author_name?:string;client_request_id?:string;intent?:string;
  id: string;
  turn_id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  created_at: number;
  attachments?: ChatAttachment[];
}

function adaptMessage(m: CfMessage, deckId: string): Message {
  return {
    id: m.id,
    conversation_id: deckId,
    turn_id:m.turn_id,
    author_user_id:m.author_user_id,author_name:m.author_name,client_request_id:m.client_request_id,intent:m.intent,
    role: m.role === 'agent' ? 'assistant' : m.role,
    content: m.content,
    // App.tsx's transformMessages reads from `contentBlocks` (Arx wire
    // shape). CF stores plain text — synthesise a single text block so
    // hydration on reload doesn't drop the message body.
    contentBlocks: [{ kind: 'text', text: m.content }],
    tulpa_id: m.role === 'agent' ? 'pp-writer' : undefined,
    created_at: new Date(m.created_at).toISOString(),
    // Off-spec extension consumed by blocksToMessages / textOrRefMessages
    // (any-typed cast there). Keeps the Message type stable.
    ...(m.attachments && m.attachments.length > 0
      ? { _imageAttachments: m.attachments }
      : {}),
  } as Message;
}

async function _getMessagesImpl(
  deckId: string,
  limit = 50,
  _offset = 0,
): Promise<Message[]> {
  // CF API uses a `before` cursor instead of offset. For Phase A
  // parity, ignore offset and return the most-recent `limit`.
  const res = await cfRequest<{ messages: CfMessage[]; has_more: boolean }>(
    'GET',
    `/decks/${deckId}/messages?limit=${limit}`,
  );
  return res.messages.map((m) => adaptMessage(m, deckId));
}

// ── Context (memory / reference files) ───────────────────────

export interface ContextItem {
  key: string;
  content: string;
  content_type?: string;
  enabled?: number;
  created_at?: string | number;
  updated_at?: string | number;
  _binary?: boolean;
  _binary_name?: string;
  _binary_size?: number;
}

async function _listContextImpl(deckId: string): Promise<ContextItem[]> {
  return cfRequest<ContextItem[]>('GET', `/decks/${deckId}/context`);
}

export async function saveContext(
  deckId: string,
  key: string,
  content: string,
  contentType?: string,
): Promise<ContextItem> {
  return cfRequest<ContextItem>('POST', `/decks/${deckId}/context`, {
    key,
    content,
    ...(contentType ? { content_type: contentType } : {}),
  });
}

export async function deleteContext(
  deckId: string,
  key: string,
): Promise<void> {
  await cfRequest<{ ok: true }>(
    'DELETE',
    `/decks/${deckId}/context/${encodeURIComponent(key)}`,
  );
}

export async function toggleContext(
  deckId: string,
  key: string,
  enabled: boolean,
): Promise<{ key: string; enabled: boolean }> {
  return cfRequest<{ key: string; enabled: boolean }>(
    'POST',
    `/decks/${deckId}/context/toggle`,
    { key, enabled },
  );
}

export async function uploadContextFile(
  deckId: string,
  file: File,
  section: string = 'context',
  skillGroup?: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ key: string; r2_key: string; content_type: string; name: string }> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error('File is too large (max 200 MB).');
  if (file.size > UPLOAD_PART_BYTES) {
    return uploadContextMultipart(file,section,skillGroup,async(action,params,body,progress)=>{
      const url = `${API}/decks/${deckId}/context/multipart?${new URLSearchParams({action,...params})}`;
      if (body instanceof Blob) return apiUpload(url,body,{onProgress:progress});
      return apiFetch(url,{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body ?? {})});
    },onProgress);
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('section', section);
  if (skillGroup) formData.append('skillGroup', skillGroup);
  // Use XHR-backed apiUpload when a progress callback is provided so
  // the composer can stream byte-level progress to the UI; otherwise
  // any fetch path would do.
  const res = await apiUpload(`${API}/decks/${deckId}/context/upload`, formData, { onProgress });
  if (!res.ok) {
    let msg = `upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(msg);
  }
  return (await res.json()) as {
    key: string;
    r2_key: string;
    content_type: string;
    name: string;
  };
}

export function contextDownloadUrl(deckId: string, key: string): string {
  return `${API}/decks/${deckId}/context/download?key=${encodeURIComponent(key)}`;
}

// ── Folders ──────────────────────────────────────────────────

interface CfFolder {
  id: string;
  workspace_id: string;
  parent_id: string | null;
  name: string;
  sort_order: number;
  deck_count?: number;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

function adaptFolder(f: CfFolder): Folder {
  return {
    id: f.id,
    workspace_id: f.workspace_id,
    parent_id: f.parent_id,
    name: f.name,
    sort_order: f.sort_order,
    deck_count: f.deck_count ?? 0,
    created_at: f.created_at,
    updated_at: f.updated_at,
    deleted_at: f.deleted_at,
  };
}

export async function getFolders(workspaceId: string): Promise<Folder[]> {
  const res = await cfRequest<{ folders: CfFolder[] }>(
    'GET',
    `/workspaces/${workspaceId}/folders`,
  );
  return (res.folders ?? []).map(adaptFolder);
}

export async function createFolder(
  workspaceId: string,
  name: string,
  parentId?: string,
): Promise<Folder> {
  const res = await cfRequest<{ folder: CfFolder }>(
    'POST',
    `/workspaces/${workspaceId}/folders`,
    { name, ...(parentId ? { parent_id: parentId } : {}) },
  );
  return adaptFolder(res.folder);
}

export async function updateFolder(
  folderId: string,
  data: { name?: string; parent_id?: string | null },
): Promise<Folder> {
  const res = await cfRequest<{ folder: CfFolder }>(
    'PATCH',
    `/folders/${folderId}`,
    data,
  );
  return adaptFolder(res.folder);
}

export async function deleteFolder(
  folderId: string,
  confirmName: string,
): Promise<{ folders_deleted: number; decks_moved: number }> {
  return cfRequest<{ folders_deleted: number; decks_moved: number }>(
    'DELETE',
    `/folders/${folderId}`,
    { confirm_name: confirmName },
  );
}

export async function moveDeckToFolder(
  deckId: string,
  folderId: string | null,
): Promise<Deck> {
  bustGetCache('decks:');
  const res = await cfRequest<{ deck: CfDeck }>('PATCH', `/decks/${deckId}`, {
    folder_id: folderId,
  });
  return adaptDeck(res.deck);
}

// ── Sharing ──────────────────────────────────────────────────

export interface ShareInfo {
  id: string;
  deck_id: string;
  share_token: string;
  title: string;
  url: string;
  view_count: number;
  created_at: number;
  updated_at: number;
}

export async function createShare(deckId: string): Promise<ShareInfo> {
  return cfRequest<ShareInfo>('POST', `/decks/${deckId}/share`);
}

export async function getShare(deckId: string): Promise<ShareInfo | null> {
  return cfRequest<ShareInfo | null>('GET', `/decks/${deckId}/share`);
}

export async function revokeShare(deckId: string): Promise<void> {
  await cfRequest<{ ok: true }>('DELETE', `/decks/${deckId}/share`);
}

// ── Profiles ─────────────────────────────────────────────────

export const listProfiles = () =>
  dedupGet('profiles', () => cfRequest<Profile[]>('GET', '/profiles'));

export const getMyProfile = () =>
  cfRequest<Profile | null>('GET', '/profiles/me');

export const updateMyProfile = (updates: Partial<Profile>) =>
  cfRequest<Profile>('PATCH', '/profiles/me', updates);

export async function uploadAvatar(file: File): Promise<Profile> {
  const formData = new FormData();
  formData.append('avatar', file);
  const res = await apiFetch(`${API}/profiles/me/avatar`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    let msg = `upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) msg = body.error.message;
    } catch {
      /* not json */
    }
    throw new Error(msg);
  }
  return (await res.json()) as Profile;
}

// ── Artifacts / Storyboard ───────────────────────────────────

export interface ArtifactItem {
  id: string;
  filename: string;
  key: string;
  created_at?: string | number;
  updated_at?: string | number;
}

export async function getArtifacts(_deckId: string): Promise<ArtifactItem[]> {
  return [];
}

export interface StoryboardData {
  cards: {
    id: string;
    slideNum: number;
    title: string;
    body: string;
    tag: string;
  }[];
  audience?: string;
  narrative?: string;
}

/** Parse `## Slide N: Title\n- bullet\n- bullet` blocks out of an
 * agent message into a storyboard. Until the backend has a server-side
 * storyboard endpoint we extract from the latest agent reply on the
 * client side. */
function parseStoryboard(content: string): StoryboardData['cards'] {
  const cards: StoryboardData['cards'] = [];
  const re = /^##\s*Slide\s*(\d+)\s*:\s*(.+)$/gim;
  const matches: { idx: number; n: number; title: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    matches.push({ idx: m.index, n: parseInt(m[1], 10), title: m[2].trim() });
  }
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].idx;
    const end = i + 1 < matches.length ? matches[i + 1].idx : content.length;
    const block = content.slice(start, end);
    const body = block.replace(/^##\s*Slide\s*\d+\s*:\s*.+$/im, '').trim();
    cards.push({
      id: `slide-${matches[i].n}`,
      slideNum: matches[i].n,
      title: matches[i].title,
      body,
      tag: '',
    });
  }
  return cards;
}

export async function getStoryboard(
  deckId: string,
): Promise<StoryboardData | null> {
  // Pull the most recent agent message and parse slide blocks out
  // of it as a fallback storyboard.
  try {
    const messages = await getMessages(deckId, 20);
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      const cards = parseStoryboard(m.content);
      if (cards.length > 0) return { cards };
    }
    return { cards: [] };
  } catch {
    return null;
  }
}

// ── Admin app_settings (Connections + Configuration tabs) ─────
//
// Backed by /api/admin/settings (apps/api/src/db/appSettingsRepo.ts).
// GET returns sensitive values masked ("abcd••••xy") so a page
// refresh doesn't leak credentials. PUT accepts cleartext and
// returns the refreshed (still-masked) map.

export interface SettingValue {
  value: string;
  source: 'default' | 'database' | 'db' | 'env';
  updated_at?: number;
}
export type AdminSettings = Record<string, SettingValue>;

export async function getAdminSettings(): Promise<AdminSettings> {
  return cfRequest<AdminSettings>('GET', '/admin/settings');
}

export async function updateAdminSettings(
  patch: Record<string, string>,
): Promise<AdminSettings> {
  return cfRequest<AdminSettings>('PUT', '/admin/settings', patch);
}

export async function testArxConnection(
  _url?: string,
): Promise<{ status: number; body: unknown }> {
  // PP-CF doesn't talk to arx — the test is N/A. Kept as a stub so
  // the existing ConnectionsManager UI code doesn't have to be
  // conditionalised; UI hides the button instead.
  throw new NotImplementedError('Arx connection test');
}

export async function forkDeck(deckId: string): Promise<{ deck_id: string; version: string }> {
  const res = await apiFetch(`${API}/decks/${deckId}/fork`, { method: 'POST' });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message || `Fork failed (${res.status})`);
  }
  return (await res.json()) as { deck_id: string; version: string };
}

export async function publishTemplate(
  _deckId: string,
  _templateId: string,
  _title: string,
): Promise<{ templateId: string; files: string[] }> {
  throw new NotImplementedError('Template publishing');
}

// ── Workspace settings (CF-only — admin Anthropic key + model) ───

export interface WorkspaceSettings {
  workspace_id: string;
  anthropic_model: string | null;
  has_anthropic_key: boolean;
  updated_at: number | null;
}

export const getWorkspaceSettings = (workspaceId: string) =>
  cfRequest<{ settings: WorkspaceSettings }>(
    'GET',
    `/workspaces/${workspaceId}/settings`,
  );

export const updateWorkspaceSettings = (
  workspaceId: string,
  patch: {
    anthropic_api_key?: string | null;
    anthropic_model?: string | null;
  },
) =>
  cfRequest<{ settings: WorkspaceSettings }>(
    'PUT',
    `/workspaces/${workspaceId}/settings`,
    patch,
  );

// ── Chat (SSE bridge) ────────────────────────────────────────
//
// PP's `chatStream` did POST /chat and read SSE inline. CF splits
// it: POST /send returns 202, then the caller subscribes to
// GET /events. We bridge so callers don't need to change.
//
// Translation of CF events → PP-style SSEEvent:
//   message       (role=user)         → { type: 'user_message', content }
//   message       (role=agent)        → ignored (we surface deltas instead)
//   activity      (kind=message_delta)→ { type: 'agent_text', text }
//   activity      (kind=agent_thinking)→ { type: 'agent_thinking' }
//   activity      (kind=turn_ended)   → { type: 'agent_complete' }
//   activity      (kind=error)        → onError(message)

interface CfActivityEvent {
  kind: string;
  payload: {
    message_id?: string;
    delta?: string;
    detail?: string;
    message?: string;
  };
}

export function chatStream(
  deckId: string,
  message: string,
  onEvent: (event: SSEEvent) => void,
  onError: (err: Error) => void,
  _attachments?: string[],
): AbortController {
  const controller = new AbortController();

  // POST /send first; if it 409s, the user has a turn already
  // running — surface that to the caller.
  void (async () => {
    try {
      const res = await apiFetch(`${API}/decks/${deckId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: message }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const t = await res.text();
        onError(new Error(t || `send failed (${res.status})`));
        return;
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      onError(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    // After send returns 202, subscribe to /events for this deck.
    const es = new EventSource(`${API}/decks/${deckId}/events`, {
      withCredentials: true,
    });
    controller.signal.addEventListener('abort', () => es.close());

    es.addEventListener('message', (ev) => {
      try {
        const m = JSON.parse((ev as MessageEvent).data) as CfMessage;
        if (m.role === 'user') {
          onEvent({ type: 'user_message', content: m.content });
        }
        // Agent message rows are surfaced via deltas; ignore here.
      } catch {
        /* ignore */
      }
    });
    es.addEventListener('activity', (ev) => {
      try {
        const a = JSON.parse((ev as MessageEvent).data) as CfActivityEvent;
        if (a.kind === 'message_delta' && typeof a.payload.delta === 'string') {
          onEvent({ type: 'agent_text', text: a.payload.delta });
        } else if (a.kind === 'agent_thinking') {
          onEvent({ type: 'agent_thinking' });
        } else if (a.kind === 'turn_ended') {
          onEvent({ type: 'agent_complete' });
          es.close();
        } else if (a.kind === 'error') {
          es.close();
          onError(new Error(a.payload.message || 'Agent error'));
        }
      } catch {
        /* ignore */
      }
    });
    es.onerror = () => {
      // Browser auto-reconnects EventSource; surface terminal errors
      // only if controller is aborted.
      if (controller.signal.aborted) es.close();
    };
  })();

  return controller;
}

export function getDeck(id: string): Promise<Deck> {
  return dedupGet('getDeck:' + String(id), () => _getDeckImpl(id)) as Promise<Deck>;
}

export function listVersions(
  deckId: string,
): Promise<{ versions: DeckVersion[]; current: string }> {
  return dedupGet('listVersions:' + String(deckId), () => _listVersionsImpl(deckId));
}

export function listDeckAgents(
  deckId: string,
): Promise<{ agents: DeckAgent[]; chat_mode: DeckChatMode }> {
  return dedupGet('listDeckAgents:' + String(deckId), () => _listDeckAgentsImpl(deckId));
}

export function listDeckMembers(deckId: string): Promise<DeckMember[]> {
  return dedupGet('listDeckMembers:' + String(deckId), () => _listDeckMembersImpl(deckId)) as Promise<DeckMember[]>;
}

export function getMessages(
  deckId: string,
  limit = 50,
  _offset = 0,
): Promise<Message[]> {
  return dedupGet('getMessages:' + String(deckId) + ':' + String(limit) + ':' + String(_offset), () => _getMessagesImpl(deckId, limit, _offset)) as Promise<Message[]>;
}

export function listContext(deckId: string): Promise<ContextItem[]> {
  return dedupGet('listContext:' + String(deckId), () => _listContextImpl(deckId)) as Promise<ContextItem[]>;
}


export interface AdminEmailConfig {
 enabled:boolean;from:string;key_configured:boolean;configured:boolean;source:'admin'|'deployment';error:string|null;
}
export function getAdminEmailConfig(){return cfRequest<AdminEmailConfig>('GET','/admin/email');}
export function saveAdminEmailConfig(body:{enabled:boolean;from:string;api_key?:string;clear_api_key?:boolean}){return cfRequest<AdminEmailConfig>('PUT','/admin/email',body);}
export async function testAdminEmailConfig(to:string):Promise<{status:'sent'|'failed'|'unknown';message:string}>{
 const response=await apiFetch('/api/admin/email/test',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({to})});
 const body=await response.json();
 if(['sent','failed','unknown'].includes(body.status))return body;
 throw new Error(body.error?.message||`Test failed (${response.status}).`);
}
